import React, { useEffect, useMemo } from 'react';
import CardLight from 'components/CardLight';
import { DocRequestEntity, DocRequestFilters } from 'types/DocRequest';
import { useTranslation } from 'react-i18next';
import PdfViewerModal from 'components/Modal/PdfViewerModal';
import useDownloadDocRequest from 'hooks/useDownloadDocRequest';
import Form from 'components/Form/Layout';
import Select from 'components/Form/FormSelect';
import TextField from 'components/Form/FormTextField';
import { Button, Col, Row } from 'react-bootstrap';
import { DocRequestTemplateCategoryType } from 'types/DocRequestTemplate';
import classNames from 'classnames';
import _ from 'lodash';
import {
  AiOutlineDelete,
} from 'react-icons/ai';
import RPagination from 'components/Pagination';
import DocsTableEdit from '../TableEdit';

type SortDirection = 'Ascending' | 'Descending' | ''; // AJUSTE: Re-declaramos el tipo para evitar dependencia circular
interface SortState {
  orderBy: string;
  sortDirection: SortDirection;
}

type InvestmentAccountOption = {
  id: number;
  name: string;
};

const INITIAL_FILTER_VALUES: DocRequestFilters = {
  categoria: null,
  estado: null,
  cuentaDeInversionID: null,
  nombre: '',
  descripcion: '',
  id: '',
  fechaLimiteQuickRange: null,
  fechaExpiracionQuickRange: null,
};

type DocsViewProps = {
  loading: boolean
  data?: DocRequestEntity[]
  setItem: ( newData: DocRequestEntity ) => void
  fetchData: ( filters: DocRequestFilters ) => void
  investmentAccounts: InvestmentAccountOption[]
  isLoadingInvestmentAccounts: boolean
  currentPage: number;
  pageSize: number;
  totalPages: number;
  onPageChange: ( page: number ) => void;
  onPageSizeChange: ( size: number ) => void;
  currentSort: SortState;
  onSortChange: ( newOrderBy: string ) => void;
};

function DocsView( {
  data, setItem, fetchData, loading,
  investmentAccounts, isLoadingInvestmentAccounts,
  currentPage, pageSize, totalPages, onPageChange, onPageSizeChange,
  currentSort, onSortChange,
}: DocsViewProps ) {
  const { t } = useTranslation();
  const {
    closeFileModal, currentFileUrl, handleViewDocument, showFileModal,
  } = useDownloadDocRequest();

  const pageIndex = currentPage - 1;
  const pageCount = totalPages;

  const previousPage = () => onPageChange( currentPage - 1 );
  const nextPage = () => onPageChange( currentPage + 1 );
  const canPreviousPage = currentPage > 1;
  const canNextPage = currentPage < totalPages;

  const gotoPage = ( updater: number | ( ( pageIndex: number ) => number ) ) => {
    let newIndex: number;

    if ( typeof updater === 'function' ) {
      newIndex = updater( pageIndex );
    } else {
      newIndex = updater;
    }

    onPageChange( newIndex + 1 );
  };

  const debouncedTextFetch = useMemo(
    () => _.debounce( ( nextFilters: DocRequestFilters ) => fetchData( nextFilters ), 500 ),
    [fetchData],
  );

  useEffect( () => () => debouncedTextFetch.cancel(), [debouncedTextFetch] );

  const quickLimitDateOptions = useMemo( () => [
    { id: 'overdue', name: t( 'admin.docsDashboard.filters.limitDate.quick.overdue' ) },
    { id: 'today', name: t( 'admin.docsDashboard.filters.limitDate.quick.today' ) },
    { id: 'next7days', name: t( 'admin.docsDashboard.filters.limitDate.quick.next7days' ) },
    { id: 'next30days', name: t( 'admin.docsDashboard.filters.limitDate.quick.next30days' ) },
    { id: 'currentMonth', name: t( 'admin.docsDashboard.filters.limitDate.quick.currentMonth' ) },
    { id: 'currentYear', name: t( 'admin.docsDashboard.filters.limitDate.quick.currentYear' ) },
  ], [t] );

  const handleTextFilterChange = (
    field: 'nombre' | 'descripcion' | 'id' | 'cuentaDeInversionName',
    value: string,
    formValues: DocRequestFilters,
  ) => {
    const nextFilters = {
      ...formValues,
      [field]: value,
    };

    const trimmedValue = value?.trim() ?? '';

    if ( !trimmedValue ) {
      debouncedTextFetch.cancel();
      fetchData( nextFilters );
      return;
    }

    if ( trimmedValue.length >= 3 ) {
      debouncedTextFetch( nextFilters );
    }
  };

  return (
    <div className={classNames( 'table-docs', {
      'app-component-loading': loading,
    } )}
    >
      <Form
        onSubmit={() => {}}
        initialValues={INITIAL_FILTER_VALUES}
        body={( form ) => (
          <>
            <Row className="g-2 mb-2">
              <Col md>
                <Select
                  field="categoria"
                  showErrors={false}
                  options={[
                    { id: DocRequestTemplateCategoryType.physical, name: t( 'admin.docs.physicalPerson' ) },
                    { id: DocRequestTemplateCategoryType.fiscal, name: t( 'admin.docs.fiscalPerson' ) },
                    { id: DocRequestTemplateCategoryType.investments, name: t( 'admin.docs.investmentsDocs' ) },
                    { id: DocRequestTemplateCategoryType.titular, name: t( 'admin.docs.titular' ) },
                  ]}
                  isMulti
                  isClearable
                  placeholder={t( 'admin.docsDashboard.filters.category.placeholder' )}
                  classNames={{
                    formGroup: 'input-filters',
                  }}
                  onChange={( value ) => fetchData( {
                    ...form.getState().values,
                    categoria: value,
                  } )}
                />
              </Col>
              <Col md>
                <Select
                  field="estado"
                  showErrors={false}
                  isMulti
                  isClearable
                  options={[
                    { id: 'PENDIENTE', name: t( 'admin.docs.status.PENDIENTE' ) },
                    { id: 'RESUELTO', name: 'RESUELTO' },
                    { id: 'PENDIENTE_REVISION', name: t( 'admin.docs.status.PENDIENTE_REVISION' ) },
                    { id: 'VALIDADO', name: t( 'admin.docs.status.VALIDADO' ) },
                    { id: 'DEVUELTO', name: t( 'admin.docs.status.DEVUELTO' ) },
                  ]}
                  placeholder={t( 'admin.docsDashboard.filters.status.placeholder' )}
                  classNames={{
                    formGroup: 'input-filters',
                  }}
                  onChange={( value ) => fetchData( { ...form.getState().values, estado: value } )}
                />
              </Col>
              <Col md>
                <Select
                  field="cuentaDeInversionID"
                  showErrors={false}
                  options={investmentAccounts}
                  isMulti
                  isClearable
                  placeholder={t( 'admin.docsDashboard.filters.investmentAccountId.placeholder' )}
                  classNames={{
                    formGroup: 'input-filters',
                  }}
                  isLoading={isLoadingInvestmentAccounts}
                  onChange={( value ) => fetchData( {
                    ...form.getState().values,
                    cuentaDeInversionID: value,
                  } )}
                />
              </Col>

              <Col md>
                <TextField
                  field="nombre"
                  showErrors={false}
                  placeholder={t( 'admin.docsDashboard.filters.name.placeholder' )}
                  classNames={{
                    formGroup: 'input-filters',
                  }}
                  onChange={( value ) => handleTextFilterChange(
                    'nombre',
                    value,
                    form.getState().values,
                  )}
                />
              </Col>
            </Row>
            <Row className="g-2 mb-3">
              <Col md>
                <TextField
                  field="descripcion"
                  showErrors={false}
                  placeholder={t( 'admin.docsDashboard.filters.description.placeholder' )}
                  classNames={{
                    formGroup: 'input-filters',
                  }}
                  onChange={( value ) => handleTextFilterChange(
                    'descripcion',
                    value,
                    form.getState().values,
                  )}
                />
              </Col>
              <Col md>
                <TextField
                  field="id"
                  showErrors={false}
                  placeholder={t( 'admin.docsDashboard.filters.id.placeholder' )}
                  classNames={{
                    formGroup: 'input-filters',
                  }}
                  onChange={( value ) => handleTextFilterChange(
                    'id',
                    value,
                    form.getState().values,
                  )}
                />
              </Col>
              <Col md>
                <Select
                  field="fechaLimiteQuickRange"
                  showErrors={false}
                  options={quickLimitDateOptions}
                  isClearable
                  placeholder={t( 'admin.docsDashboard.filters.limitDate.placeholder' )}
                  classNames={{
                    formGroup: 'input-filters',
                  }}
                  onChange={( value ) => fetchData( {
                    ...form.getState().values,
                    fechaLimiteQuickRange: value,
                  } )}
                />
              </Col>
              <Col md>
                <Select
                  field="fechaExpiracionQuickRange"
                  showErrors={false}
                  placeholder={t( 'admin.docsDashboard.filters.expirationDate.placeholder' )}
                  options={quickLimitDateOptions}
                  isClearable
                  classNames={{
                    formGroup: 'input-filters',
                  }}
                  onChange={( value ) => fetchData( {
                    ...form.getState().values,
                    fechaExpiracionQuickRange: value,
                  } )}
                />
              </Col>
            </Row>
            <Row className="gx-2 mb-3">
              <Col md className="d-flex align-items-end justify-content-end">
                <Button
                  variant="outline-primary"
                  className="py-2 px-4 text--normal btn--rounded d-flex align-items-center btn-sm"
                  onClick={() => {
                    debouncedTextFetch.cancel();
                    form.reset( INITIAL_FILTER_VALUES );
                    fetchData( INITIAL_FILTER_VALUES );
                  }}
                >
                  <AiOutlineDelete size={15} className="me-2" />
                  {t( 'admin.docsDashboard.filters.clear' )}
                </Button>
              </Col>
            </Row>
          </>
        )}
      />

      <CardLight>
        <DocsTableEdit
          data={data}
          reloadData={setItem}
          handleViewDocument={handleViewDocument}
          currentSort={currentSort}
          onSortChange={onSortChange}
        />
        {showFileModal && currentFileUrl && (
        <PdfViewerModal url={currentFileUrl} onClose={closeFileModal} />
        )}
      </CardLight>

      <RPagination
        previousPage={previousPage}
        nextPage={nextPage}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        setPageSize={onPageSizeChange}
        pageSize={pageSize}
        pageCount={pageCount}
        pageIndex={pageIndex}
        gotoPage={gotoPage}
        options={['10', '20', '30']}
      />
    </div>
  );
}

export default DocsView;
